import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Trash2, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { getOrCreateSessionId } from "../lib/kioskConfig";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang || "en", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function History() {
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => getOrCreateSessionId());

  const load = () => {
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/chat/history/${sessionId}`)
      .then((r) => setMessages(Array.isArray(r.data) ? r.data : []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, [sessionId]);

  const clear = () => {
    // Start a fresh session — old messages remain in DB but no longer accessible here.
    localStorage.removeItem("sakhi_session");
    getOrCreateSessionId();
    setMessages([]);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">{t("my_history")}</h1>
          <p className="text-sm text-muted-foreground mt-2">Session: {sessionId.slice(0, 20)}...</p>
        </div>
        {messages.length > 0 && (
          <Button
            data-testid="history-clear-btn"
            variant="outline"
            onClick={clear}
            className="rounded-full h-11 gap-2 border-2 bg-white"
          >
            <Trash2 className="w-4 h-4" /> {t("clear_history")}
          </Button>
        )}
      </div>

      {loading && <div className="text-muted-foreground">Loading...</div>}

      {!loading && messages.length === 0 && (
        <div className="text-center py-16 fade-up">
          <div className="w-20 h-20 mx-auto rounded-full bg-secondary/30 flex items-center justify-center mb-5">
            <Sparkles className="w-10 h-10 text-primary" strokeWidth={2.5} />
          </div>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">{t("no_history")}</p>
          <Link to="/chat" className="pill-btn pill-btn-primary mt-8 inline-flex gap-2">
            <MessageCircle className="w-5 h-5" /> {t("chat_cta")}
          </Link>
        </div>
      )}

      {!loading && messages.length > 0 && (
        <div className="space-y-3" data-testid="history-list">
          {messages.map((m, i) => (
            <div
              key={m.id || i}
              data-testid={`history-msg-${m.role}`}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} fade-up`}
              style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}
            >
              <div
                className={`max-w-[85%] px-5 py-3 rounded-3xl text-base leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-primary text-white rounded-tr-md"
                    : "bg-white border border-border text-foreground rounded-tl-md"
                }`}
              >
                <div className="text-xs opacity-70 mb-1">
                  {m.role === "user" ? "You" : "Sakhi Didi"} • {formatDate(m.created_at, lang)}
                </div>
                {m.text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
